/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../../components/Modal/Modal';
import { PrimaryButton, GhostButton } from '../../../../../styles/ui/Button';
import { rescheduleInterview } from '../../../../../services/hr/recruitment/interviewsApi';
import { defaultModalityForRound } from '../../constants';
import { toIsoWithOffset } from '../../utils/format';
import { readApiError, recruitmentErrorMessage } from '../../utils/apiError';
import InterviewScheduleFields from './InterviewScheduleFields';

// ---------------------------------------------------------------------------
// Remarcar a entrevista ativa (POST /interviews/:id/reschedule). A atual vira
// REMARCADO (histórica) e nasce um registro novo (ativo) da mesma rodada — a UI
// deixa isso explícito. Mantém o stage.
// ---------------------------------------------------------------------------

const Warn = styled.p`
   margin: 0 0 14px;
   font-size: 12.5px;
   line-height: 1.45;
   padding: 10px 12px;
   border-radius: 8px;
   background: rgba(133, 79, 11, 0.1);
   color: #854f0b;
`;

export default function RescheduleInterviewModal({
   isOpen,
   onClose,
   interview,
   onSaved,
}) {
   const [modality, setModality] = useState(defaultModalityForRound());
   const [interviewerId, setInterviewerId] = useState('');
   const [scheduledAt, setScheduledAt] = useState('');
   const [errors, setErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   useEffect(() => {
      if (isOpen && interview) {
         setModality(
            interview.modality ?? defaultModalityForRound(interview.round),
         );
         setInterviewerId(
            interview.interviewer_user_id
               ? String(interview.interviewer_user_id)
               : '',
         );
         setScheduledAt('');
         setErrors({});
         setIsSubmitting(false);
      }
   }, [isOpen, interview]);

   const handleConfirm = async () => {
      if (isSubmitting) return;
      if (!scheduledAt) {
         setErrors({ scheduled_at: 'Informe a nova data e hora.' });
         return;
      }

      setIsSubmitting(true);
      setErrors({});
      try {
         const { message } = await rescheduleInterview(interview.id, {
            scheduledAt: toIsoWithOffset(scheduledAt),
            modality,
            interviewerUserId: interviewerId ? Number(interviewerId) : undefined,
         });
         toast.success(message);
         onSaved?.();
         onClose?.();
      } catch (err) {
         const { fieldErrors } = readApiError(err);
         if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
         } else {
            toast.error(
               recruitmentErrorMessage(err, 'Erro ao remarcar a entrevista.'),
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isSubmitting}>
            Cancelar
         </GhostButton>
         <PrimaryButton
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
         >
            {isSubmitting ? 'Remarcando…' : 'Remarcar entrevista'}
         </PrimaryButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={`Remarcar Entrevista ${interview?.round ?? ''}`}
         maxWidth="560px"
         footer={footer}
      >
         <Warn>
            A entrevista atual passa para o histórico como <strong>Remarcado</strong>{' '}
            e uma nova entrevista ativa é criada com a data informada.
         </Warn>
         <InterviewScheduleFields
            modality={modality}
            onModality={setModality}
            interviewerId={interviewerId}
            onInterviewer={setInterviewerId}
            scheduledAt={scheduledAt}
            onScheduledAt={setScheduledAt}
            disabled={isSubmitting}
            errors={errors}
         />
      </Modal>
   );
}

RescheduleInterviewModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   interview: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      round: PropTypes.number,
      modality: PropTypes.string,
      interviewer_user_id: PropTypes.oneOfType([
         PropTypes.string,
         PropTypes.number,
      ]),
      interviewer: PropTypes.shape({ name: PropTypes.string }),
   }),
   onSaved: PropTypes.func,
};
